import { Group, Stack } from "@mantine/core"
import { PaginationState, RowSelectionState } from "@tanstack/react-table"
import { ColumnManager } from "@tfg/ui"
import { Dispatch, SetStateAction, useState } from "react"
import { useTranslation } from "react-i18next"

import { ProductResponses } from "@tfg/types/endpoints/products"
import { fetchDataOptionsValidationType } from "@tfg/types/schemas/products"

import { ProductFilters } from "~/components/ProductFilters"
import { ProductSearch } from "~/components/ProductSearch"
import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { CustomTable } from "~/components/table/CustomTable"
import { FindProductsCatalogRequestFiltersDto } from "~/gen/api"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { AgecBulkActions } from "~/pages/Brand/Regulations/AGEC/AgecBulkActions.tsx"
import { useAgecColumns } from "~/pages/Brand/Regulations/AGEC/columns.tsx"

interface Props {
  data: ProductResponses.GetProducts
  fetchDataOptions: fetchDataOptionsValidationType
  setPagination: Dispatch<SetStateAction<PaginationState>>
  filtersValue: FindProductsCatalogRequestFiltersDto
  setFiltersValue: Dispatch<
    SetStateAction<FindProductsCatalogRequestFiltersDto>
  >
  isFetching: boolean
}

export const ProductsAGECTable = ({
  data,
  fetchDataOptions,
  setPagination,
  filtersValue,
  setFiltersValue,
  isFetching,
}: Props) => {
  const columns = useAgecColumns()
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState<boolean>(false)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const { t } = useTranslation("regulations")
  const columnManagerTranslations = useGetColumnManagerTranslations()

  const table = useCustomReactTable({
    state: {
      pagination: fetchDataOptions,
      rowSelection,
    },

    initialColumnPinning: { left: ["name"] },
    manualPagination: true,
    data: data.products,
    getRowId: (row) => row.id.toString(),
    columns,
    rowCount: data.totalProducts,
    onPaginationChange: setPagination,

    onRowSelectionChange: setRowSelection,
  })

  return (
    <Stack>
      <Group>
        <ProductSearch
          filtersValue={filtersValue}
          setFiltersValue={setFiltersValue}
          isFetching={isFetching}
        />
        <ProductFilters<FindProductsCatalogRequestFiltersDto>
          setFiltersValue={setFiltersValue}
          filtersValue={filtersValue}
          extraFilters={[
            {
              label: t("lists.placeholders.agec"),
              key: "agecCompliance",
              type: "select",
              options: [
                { value: "yes", label: t("lists.placeholders.yes") },
                { value: "no", label: t("lists.placeholders.no") },
              ],
            },
          ]}
        />
        <ColumnManager
          table={table}
          translations={columnManagerTranslations}
          isOpen={isColumnManagerOpen}
          setIsOpen={setIsColumnManagerOpen}
        />
        <AgecBulkActions table={table} />
      </Group>
      <CustomTable
        table={table}
        pagination={true}
        manualPaginationCount={data.totalProducts}
        layout="fixed"
        setOpenColumnManager={setIsColumnManagerOpen}
      />
    </Stack>
  )
}
